<template>
    <nav aria-label="Skip links">
        <ul id="site-ada-menu" class="ada-menu">
            <li
                v-for="(item, key) in items"
                :key="key"
            >
                <a v-if="isLink(item[1])"
                    class="ada-menu__item"
                    :href="item[1]" 
                >{{ item[0] }}</a>
                <button v-else 
                    class="ada-menu__item"
                    :data-tabto="item[1]"
                    @click="handleClick"
                >{{ item[0] }}</button>
            </li>
        </ul>
    </nav>
</template>

<script>

function elIsFocusable(el) {
    const focusableEls = ['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA'];
    const style = window.getComputedStyle(el);
    const isVisible = style.display !== 'none' && style.visibility !== 'hidden';
    
    // Check if element is naturally focusable
    if (focusableEls.includes(el.tagName) && isVisible) {
        return true;
    }
    
    // Check if element has tabindex attribute
    if (el.hasAttribute('tabindex') && isVisible) {
        const tabindex = parseInt(el.getAttribute('tabindex'), 10);
        return tabindex >= 0 || tabindex === -1;
    }
    
    return false;
}

function getSelectorFromDestination(destination) {
    if (!destination || typeof destination !== 'string') {
        return null;
    }
    if (destination.includes(':')) {
        return destination.slice(destination.indexOf(':') + 1);
    }
    return destination;
}

function getInPageTarget(destination) {
    const selector = getSelectorFromDestination(destination);
    if (!selector || typeof document === 'undefined') {
        return null;
    }
    let elements;
    try {
        elements = document.querySelectorAll(selector);
    } catch (e) {
        // An invalid selector on one item must not throw and empty the whole menu
        return null;
    }
    for (let i = 0; i < elements.length; i++) {
        if (isElementVisible(elements[i])) {
            return elements[i];
        }
    }
    return null;
}

function isElementVisible(el) {
    if (!el) {
        return false;
    }
    if (typeof window === 'undefined') {
        return false;
    }
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) {
        return false;
    }
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
}

function isExternalNavigation(destination) {
    return typeof destination === 'string' && (
        destination.startsWith('http://') ||
        destination.startsWith('https://') ||
        destination.startsWith('/')
    );
}

function inPageTargetExists(destination) {
    const el = getInPageTarget(destination);
    return isElementVisible(el);
}

function getFilteredItems() {
    const items = window.ada_menu_items || [];
    return items.filter((item) => {
        const destination = item[1] || '';
        if (isExternalNavigation(destination)) {
            return true;
        }
        return inPageTargetExists(destination);
    });
}


export default {
    data() {
        return {
            items: [],
            refreshTimers: [],
        }
    },
    mounted() {
        this.refreshItems();
        this.scheduleRefreshes();

        this.onAdaMenuKeydown = (e) => {
            const key = e.which || e.keyCode;
            if (key === 13 && e.target.dataset.tabto) {
                e.preventDefault();
                this.handleTabTo(e.target.dataset.tabto);
            }
        };
        document.addEventListener('keydown', this.onAdaMenuKeydown);
    },
    beforeDestroy() {
        this.refreshTimers.forEach(clearTimeout);
        window.removeEventListener('load', this.refreshItems);
        if (this.onAdaMenuKeydown) {
            document.removeEventListener('keydown', this.onAdaMenuKeydown);
        }
    },
    methods: {
        refreshItems() {
            const nextItems = getFilteredItems();
            if (JSON.stringify(nextItems) !== JSON.stringify(this.items)) {
                this.items = nextItems;
            }
        },
        scheduleRefreshes() {
            // Bounded rechecks for async targets like Live Chat, so the list settles instead of tracking all page activity
            if (document.readyState !== 'complete') {
                window.addEventListener('load', this.refreshItems, { once: true });
            }
            this.refreshTimers = [1000, 3000, 10000].map((delay) => setTimeout(this.refreshItems, delay));
        },
        makeElementFocusable(el) {
            // Make an element focusable if it isn't naturally focusable
            if (el && !elIsFocusable(el) && !el.hasAttribute('tabindex')) {
                el.setAttribute('tabindex', '-1');
            }
        },
        handleClick(e) {
            // Handle click events from screen readers
            const button = e.currentTarget;
            if (button && button.dataset.tabto) {
                e.preventDefault();
                this.handleTabTo(button.dataset.tabto);
            }
        },
        handleTabTo(tabto) {
            if(tabto.includes(':')) {
                const split = tabto.split(':');
                if(this[split[0]] && split.length == 2) {
                    this[split[0]](split[1])
                }
            } else {
                const target = getInPageTarget(tabto);
                if (target) {
                    // Make the target focusable if it isn't naturally focusable
                    this.makeElementFocusable(target);
                    // Focus the target element
                    target.focus();
                }
            }
        },
        focusFirstChild(parent) {
            const container = document.querySelector(parent);
            if (!container) return;
            const firstChild = container.firstElementChild;
            if (!firstChild) return;
            if (!firstChild.hasAttribute('tabindex')) firstChild.setAttribute('tabindex', '-1');
            setTimeout(() => firstChild.focus(), 300);
        },
        focusMainHeading(parent) {
            const region = getInPageTarget(parent);
            if (!region) return;
            // Focus the region's heading so screen readers announce it. VoiceOver skips a
            // visually-hidden H1 when focus lands on the container itself. Only consider a
            // visible/rendered heading — an h1 belonging to a hidden modal elsewhere in the
            // region (e.g. display:none until opened) can't receive focus, which would
            // silently no-op the skip link instead of falling back to the region.
            const heading = Array.from(region.querySelectorAll('h1')).find(isElementVisible) || region;
            this.makeElementFocusable(heading);
            heading.focus();
        },
        firstFocusableElement(parent) {
            let focusOn = false;
            const elements = document.querySelectorAll(parent + ' *');
            for (let i = 0, max = elements.length; i < max; i++) {
                const target = elements[i];
                if (elIsFocusable(target)) {
                    focusOn = target;
                    break;
                }
            }
            if (focusOn) focusOn.focus();
        },
        isLink(to) {
            if (typeof to !== 'string') {
                return false;
            }
            // Real navigations render as <a href>: absolute/relative URLs...
            if (to.startsWith('/') || to.startsWith('http://') || to.startsWith('https://')) {
                return true;
            }
            // ...or a bare "#id" fragment. Selector lists (e.g. 'main, [role="main"], #main-content')
            // and prefixed JS actions (e.g. 'focusFirstChild:...') render as <button> so the click
            // handler can resolve the fallback chain, make the target focusable, and move focus.
            return to.startsWith('#') && !/[\s,]/.test(to);
        }
    }
}
</script>

<style>
    .ada-menu {
        height: 0;
        left: 0;
        position: fixed;
        z-index: 999;
        top: 37px;
    }

    .ada-menu .ada-menu__item {
        -webkit-appearance: none; /* stylelint-disable-line property-no-vendor-prefix */
        background-color: #fff !important; /* stylelint-disable-line declaration-no-important */
        left: -200px;
        min-width: 125px;
        max-width: 200px;
        opacity: 0;
        padding: 10px;
        position: absolute;
        text-align: center;
        top: 0;
        overflow: hidden;
    }

    .ada-menu .ada-menu__item:focus {
        left: 0;
        opacity: 1;
    }
</style>
